/**
 * Champion sprite-sheet loader (#223 Task 4) — resolves a champion id to a
 * bundled sprite-sheet PNG URL + its parsed metadata, entirely client-side
 * (presentation layer). The character analogue of `arenaAssets.ts` /
 * `champions/abilityIcons.ts`.
 *
 * BOUNDARY: sprites are cosmetic/presentation and MUST NOT touch the sim or any
 * hash. The metadata schema lives HERE in the client, NOT in `@pit/protocol` —
 * it describes how to slice a sheet, which is a rendering concern. Deleting the
 * PNGs returns entities to their procedural body draw (the rip-out contract);
 * a malformed `meta.json` logs a loud console error and ALSO falls back to
 * procedural — it never crashes the renderer.
 *
 * ASSET RESOLUTION: a champion's sheet lives at
 * `packages/content/assets/characters/<slug>/<slug>.png` with a sibling
 * `<slug>.json` metadata file. Vite's `import.meta.glob` maps the PNG to a
 * content-hashed URL and inlines the JSON as raw text at build time. Absent art
 * → `null` → procedural fallback. Ships safely before any sprite art exists.
 */

import { z } from "zod";
import { ALL_CONTENT_CHAMPIONS } from "../champions/catalog.js";

/**
 * The closed set of sprite animation names (bible §6 / `style.json`
 * sprites.animations). The client CHOOSES among these at render time from
 * snapshot state; the sheet provides a block per baked animation.
 */
export const SPRITE_ANIMATION_NAMES = ["idle", "walk", "cast", "attack", "hurt", "death"] as const;
export type SpriteAnimationName = (typeof SPRITE_ANIMATION_NAMES)[number];

/**
 * One animation's placement in the sheet. The sheet is a grid of `frameWidth ×
 * frameHeight` cells: an animation occupies `facings` consecutive rows starting
 * at `baseRow` (row `baseRow + facing` is that facing's strip), and `frames`
 * columns. `fps` is the playback rate the render clock uses.
 */
export const SpriteAnimationBlockSchema = z.strictObject({
  baseRow: z.number().int().gte(0),
  frames: z.number().int().gte(1),
  fps: z.number().gt(0),
});
export type SpriteAnimationBlock = z.infer<typeof SpriteAnimationBlockSchema>;

/**
 * Sprite-sheet metadata (presentation-only). `idle` is mandatory (the always-
 * available fallback animation); the rest are optional per what was baked.
 * Unknown keys are rejected (strict) so a typo can't silently no-op.
 */
export const SpriteSheetMetaSchema = z.strictObject({
  version: z.literal(1),
  /** Champion slug this sheet belongs to (matches the folder name). */
  champion: z.string().min(1),
  frameWidth: z.number().int().gte(1),
  frameHeight: z.number().int().gte(1),
  facings: z.number().int().gte(1),
  /** Ground-contact circle radius in sheet px (from the sprite-render calibration). */
  footprintRadiusPx: z.number().gte(0),
  animations: z.strictObject({
    idle: SpriteAnimationBlockSchema,
    walk: SpriteAnimationBlockSchema.optional(),
    cast: SpriteAnimationBlockSchema.optional(),
    attack: SpriteAnimationBlockSchema.optional(),
    hurt: SpriteAnimationBlockSchema.optional(),
    death: SpriteAnimationBlockSchema.optional(),
  }),
});
export type SpriteSheetMeta = z.infer<typeof SpriteSheetMetaSchema>;

/**
 * Parse + validate raw sprite-meta JSON. Returns `null` — and logs a LOUD
 * console error — on malformed JSON or schema failure, so the renderer falls
 * back to the procedural body draw instead of crashing (the rip-out contract).
 * `label` is only for the diagnostic message.
 */
export function parseSpriteMeta(raw: string, label: string): SpriteSheetMeta | null {
  let json: unknown;
  try {
    json = JSON.parse(raw);
  } catch (e) {
    console.error(
      `[pit] champion sprite meta "${label}" is not valid JSON — falling back to procedural rendering:`,
      e,
    );
    return null;
  }
  const parsed = SpriteSheetMetaSchema.safeParse(json);
  if (!parsed.success) {
    console.error(
      `[pit] champion sprite meta "${label}" failed validation — falling back to procedural rendering:`,
      parsed.error.issues,
    );
    return null;
  }
  return parsed.data;
}

/**
 * Champion id → filesystem slug, DERIVED from the same loaded content catalog
 * `catalog.ts` already builds at module-init (#572; the old hand-maintained,
 * single-entry `SLUG_BY_CHAMPION` map required a manual edit per champion —
 * exactly the N-independent-copies problem #546 names). Every champion in
 * content has a `slug`; this is every one of them, not just the ones with
 * baked sheets (see `KNOWN_SPRITE_CHAMPION_IDS` below for that narrower set).
 */
const SLUG_BY_CHAMPION: ReadonlyMap<string, string> = new Map(
  ALL_CONTENT_CHAMPIONS.map((c) => [c.id, c.slug]),
);

/**
 * Build-time map of every champion sheet PNG → its bundled URL. Eager + `?url`
 * so each entry is a ready string. Keyed by the `<slug>` folder segment.
 *
 * IMPORTANT: `import.meta.glob(...)` MUST be written as this literal call
 * expression. Vite transforms it only via static analysis of the literal form;
 * reading `.glob` off a cast/aliased `import.meta` hides the call from the
 * transformer, leaving it `undefined` at build → an empty map → assets never
 * bundle (the original #181 bug). Under vitest the literal form resolves real
 * committed URLs; absent files simply produce no entry (rip-out contract).
 */
function loadSheetUrlMap(): ReadonlyMap<string, string> {
  const modules = import.meta.glob<string>(
    "../../../../packages/content/assets/characters/**/*.png",
    { eager: true, query: "?url", import: "default" },
  );
  return bySlug(modules);
}

/**
 * Build-time map of every champion sheet metadata JSON → its RAW text. `?raw`
 * (not a parsed import) so a malformed file becomes a runtime fallback via
 * `parseSpriteMeta`, not a build error. Same literal-call rule as above (#181).
 */
function loadMetaRawMap(): ReadonlyMap<string, string> {
  const modules = import.meta.glob<string>(
    "../../../../packages/content/assets/characters/**/*.json",
    { eager: true, query: "?raw", import: "default" },
  );
  return bySlug(modules);
}

/** Index glob results by the `<slug>` folder segment (first sorted file wins). */
function bySlug(modules: Record<string, string>): ReadonlyMap<string, string> {
  const map = new Map<string, string>();
  for (const path of Object.keys(modules).sort()) {
    const slug = slugFromPath(path);
    const value = modules[path];
    if (slug !== null && value !== undefined && !map.has(slug)) map.set(slug, value);
  }
  return map;
}

/**
 * Extract the `<slug>` folder segment from a glob module path.
 * e.g. ".../assets/characters/warrior/warrior.png" → "warrior".
 * Returns null if the path doesn't sit under a `characters/` segment.
 */
function slugFromPath(path: string): string | null {
  const marker = "/characters/";
  const idx = path.lastIndexOf(marker);
  if (idx === -1) return null;
  const tail = path.slice(idx + marker.length);
  const seg = tail.split("/")[0];
  return seg === undefined || seg.length === 0 ? null : seg;
}

const SHEET_URL_BY_SLUG: ReadonlyMap<string, string> = loadSheetUrlMap();
const META_RAW_BY_SLUG: ReadonlyMap<string, string> = loadMetaRawMap();

/**
 * Champion ids that have a BAKED sprite sheet actually committed — i.e. both a
 * PNG and metadata JSON exist under `packages/content/assets/characters/<slug>/`
 * (#572). This is NOT "every champion in content": most champions still fall
 * back to the procedural body draw (the file's rip-out contract). Presence is
 * determined purely by cross-referencing each content champion's `slug`
 * against `SHEET_URL_BY_SLUG` — the actual on-disk asset glob — never by the
 * champion-identity map, which only tracks id→slug, not sheet existence.
 */
export const KNOWN_SPRITE_CHAMPION_IDS: readonly string[] = ALL_CONTENT_CHAMPIONS.filter((c) =>
  SHEET_URL_BY_SLUG.has(c.slug),
).map((c) => c.id);

/** A resolved champion sprite sheet: the bundled PNG url + parsed metadata. */
export interface ChampionSpriteSheet {
  readonly slug: string;
  readonly sheetUrl: string;
  readonly meta: SpriteSheetMeta;
}

/** The slug assigned to a champion id, or `null` if none. */
export function championSlug(championId: string): string | null {
  return SLUG_BY_CHAMPION.get(championId) ?? null;
}

/**
 * Resolve `championId`'s sprite sheet (bundled url + parsed meta), or `null`
 * when: the champion has no slug, no PNG is committed, no metadata is committed,
 * OR the metadata is malformed (logged). Callers fall back to the procedural
 * body draw on `null` — the rip-out contract, never a crash.
 */
export function resolveChampionSheet(championId: string): ChampionSpriteSheet | null {
  const slug = SLUG_BY_CHAMPION.get(championId);
  if (slug === undefined) return null;
  const sheetUrl = SHEET_URL_BY_SLUG.get(slug);
  const rawMeta = META_RAW_BY_SLUG.get(slug);
  if (sheetUrl === undefined || rawMeta === undefined) return null;
  const meta = parseSpriteMeta(rawMeta, slug);
  if (meta === null) return null;
  return { slug, sheetUrl, meta };
}
